import { useState, type ReactNode } from 'react';
import { t, type BannerAction, type BannerTone } from '@bms2/shared';
import { AlertBanner, type AlertBannerProps } from './AlertBanner';

/** What a page says after a save: the banner's own tone, its words, and an action of its own when it has one. */
export interface Notice {
  tone: BannerTone;
  text: AlertBannerProps['message'];
  action?: BannerAction;
}

/**
 * The notice a page shows after a save (piece 3).
 *
 * 🔨 A page holds this instead of a state of its own beside an Alert Banner:
 * fifty-one pages wrote that pair out by hand, each with its own Dismiss that
 * set the state back to null. The banner comes back as null while there is no
 * notice, Dismiss clears it, and an action the page passes replaces Dismiss.
 * The notice itself comes third, for a page whose open window reads a refusal
 * inside it (piece 5, *Reason window*).
 */
export function useNotice(): [ReactNode, (notice: Notice | null) => void, Notice | null] {
  const [notice, setNotice] = useState<Notice | null>(null);
  const banner = notice ? (
    <AlertBanner tone={notice.tone} message={notice.text} action={notice.action ?? { label: t('modal.dismiss'), onAction: () => setNotice(null) }} />
  ) : null;
  return [banner, setNotice, notice];
}
